import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Search } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { format, parseISO, addDays } from "date-fns";

export const InvoiceForm = ({ mode = "create" }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const gigId = searchParams.get('gig');
  
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [gigs, setGigs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [recipientSuggestions, setRecipientSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const [formData, setFormData] = useState({
    gig_id: gigId || "",  // legacy — mirrors gig_ids[0]; backend still accepts it
    gig_ids: gigId ? [gigId] : [],  // bundle of linked gigs (1+; can grow via "Add another gig")
    recipient_type: "venue",
    recipient_name: "",
    recipient_email: "",
    recipient_address: "",
    issue_date: format(new Date(), 'yyyy-MM-dd'),
    payment_terms_days: null, // Will use user default
    line_items: [],
    vat_rate: 0,
    currency: "GBP",
    notes: "",
    invoice_number: ""  // Empty = use auto-generated server-side
  });
  // Suggested next invoice number (preview, fetched on create-mode mount).
  // Displayed as placeholder so the user knows what it'll be if left blank.
  const [suggestedInvoiceNumber, setSuggestedInvoiceNumber] = useState("");
  // "Save as default contact for X?" prompt state. Populated by handleGigChange
  // when the linked gig has no saved venue/band contact yet.
  const [defaultContactCandidate, setDefaultContactCandidate] = useState(null);
  const [savingDefaultContact, setSavingDefaultContact] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchGigs();
    if (mode === "create") {
      apiClient.get("/invoices/next-number-preview")
        .then(r => setSuggestedInvoiceNumber(r.data?.next_invoice_number || ""))
        .catch(() => {});
    }
    
    if (mode === "edit" && id) {
      fetchInvoice(id);
    }
  }, [mode, id]);

  // Fetch all recipient suggestions once on mount (type selector is hidden;
  // backend's `type=all` merges venues + band leaders + agents + FA + Xero).
  useEffect(() => {
    if (mode === "create") {
      fetchRecipientSuggestions("all");
    }
  }, [mode]);

  useEffect(() => {
    // Auto-populate from gig if selected
    if (formData.gig_id && formData.line_items.length === 0 && mode === "create") {
      const gig = gigs.find(g => g.gig_id === formData.gig_id);
      if (gig) {
        autoPopulateFromGig(gig);
      }
    }
  }, [formData.gig_id, gigs]);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get("/user/invoice-settings");
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings", error);
    }
  };

  const fetchGigs = async () => {
    try {
      const response = await apiClient.get("/gigs");
      setGigs(response.data);
    } catch (error) {
      console.error("Failed to fetch gigs", error);
    }
  };

  const fetchRecipientSuggestions = async (type) => {
    setLoadingSuggestions(true);
    try {
      const response = await apiClient.get(`/invoices/recipient-suggestions?recipient_type=${type}`);
      setRecipientSuggestions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch suggestions", error);
      setRecipientSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      recipient_name: suggestion.name || "",
      recipient_email: suggestion.email || "",
      recipient_address: suggestion.address || ""
    }));
  };

  const fetchInvoice = async (invoiceId) => {
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}`);
      const inv = response.data;
      setFormData({
        gig_id: inv.gig_id || "",
        recipient_type: inv.recipient_type,
        recipient_name: inv.recipient_name,
        recipient_email: inv.recipient_email || "",
        recipient_address: inv.recipient_address || "",
        issue_date: inv.issue_date,
        payment_terms_days: null,
        line_items: inv.line_items || [],
        vat_rate: inv.vat_rate || 0,
        currency: inv.currency || "GBP",
        notes: inv.notes || ""
      });
    } catch (error) {
      toast.error("Failed to fetch invoice");
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  };

  const autoPopulateFromGig = async (gig) => {
    const items = [];

    // Format gig date
    let gigDateStr = "";
    try {
      gigDateStr = format(parseISO(gig.date), 'dd MMM yyyy');
    } catch {
      gigDateStr = gig.date || "";
    }

    // Add performance fee
    if (gig.fee) {
      items.push({
        description: `Performance fee - ${gig.artist_name} at ${gig.venue_name}, ${gigDateStr}`,
        quantity: 1,
        unit_price: gig.fee,
        amount: gig.fee
      });
    }

    // Add expenses
    if (gig.expenses && gig.expenses > 0) {
      items.push({
        description: "Travel & expenses",
        quantity: 1,
        unit_price: gig.expenses,
        amount: gig.expenses
      });
    }

    // Resolve invoice recipient via the backend (honours per-gig override).
    let resolved = null;
    try {
      resolved = (await apiClient.get(`/gigs/${gig.gig_id}/invoice-recipient`)).data;
    } catch {}

    const hasSavedContact = resolved &&
      (resolved.source === "venue" || resolved.source === "band" ||
       resolved.source === "collaborator" || resolved.source === "custom");

    setFormData(prev => ({
      ...prev,
      recipient_name: resolved?.name || gig.venue_name || prev.recipient_name,
      recipient_email: prev.recipient_email || resolved?.email || "",
      line_items: items,
      gig_ids: prev.gig_ids.includes(gig.gig_id) ? prev.gig_ids : [gig.gig_id, ...prev.gig_ids],
    }));

    if (!hasSavedContact && (!gig.invoice_recipient_type || gig.invoice_recipient_type === "auto")) {
      // No explicit override and no saved default — offer to save one.
      let venueData = null, bandData = null;
      if (gig.venue_id) { try { venueData = (await apiClient.get(`/venues/${gig.venue_id}`)).data; } catch {} }
      if (gig.band_id) { try { bandData = (await apiClient.get(`/bands/${gig.band_id}`)).data; } catch {} }
      setDefaultContactCandidate({
        gigId: gig.gig_id,
        venue: venueData ? { id: venueData.venue_id || gig.venue_id, name: venueData.name } : null,
        band: bandData ? { id: bandData.band_id || gig.band_id, name: bandData.name } : null,
      });
    } else {
      setDefaultContactCandidate(null);
    }
  };

  const handleGigChange = async (gigId) => {
    // Treat "none" as no gig linked. Resets the entire bundle to a single gig
    // (or none) and clears line items — same semantics as before, but now
    // tracks gig_ids alongside gig_id.
    const actualGigId = gigId === "none" ? "" : gigId;
    setFormData(prev => ({
      ...prev,
      gig_id: actualGigId,
      gig_ids: actualGigId ? [actualGigId] : [],
      line_items: [],
    }));
    setDefaultContactCandidate(null);
    if (!actualGigId) return;

    const gig = gigs.find(g => g.gig_id === actualGigId);
    if (!gig) return;

    // Resolve via backend (honours per-gig override). Prefill blank fields only.
    let resolved = null;
    try {
      resolved = (await apiClient.get(`/gigs/${actualGigId}/invoice-recipient`)).data;
    } catch {}

    const hasSavedContact = resolved &&
      (resolved.source === "venue" || resolved.source === "band" ||
       resolved.source === "collaborator" || resolved.source === "custom");

    if (resolved?.name || resolved?.email) {
      setFormData(prev => ({
        ...prev,
        recipient_name: prev.recipient_name || resolved.name || "",
        recipient_email: prev.recipient_email || resolved.email || "",
      }));
    }

    if (!hasSavedContact && (!gig.invoice_recipient_type || gig.invoice_recipient_type === "auto")) {
      let venueData = null, bandData = null;
      if (gig.venue_id) { try { venueData = (await apiClient.get(`/venues/${gig.venue_id}`)).data; } catch {} }
      if (gig.band_id) { try { bandData = (await apiClient.get(`/bands/${gig.band_id}`)).data; } catch {} }
      setDefaultContactCandidate({
        gigId: actualGigId,
        venue: venueData ? { id: venueData.venue_id || gig.venue_id, name: venueData.name } : null,
        band: bandData ? { id: bandData.band_id || gig.band_id, name: bandData.name } : null,
      });
    } else {
      setDefaultContactCandidate(null);
    }
  };

  // Dismiss the "Save as default contact" prompt — remembered per-gig in localStorage
  // so it doesn't keep reappearing on the same gig.
  const dismissDefaultContactPrompt = () => {
    if (defaultContactCandidate?.gigId) {
      try {
        localStorage.setItem(`fretless:dismiss-default-${defaultContactCandidate.gigId}`, "1");
      } catch {}
    }
    setDefaultContactCandidate(null);
  };

  // Save the manually-typed recipient as the venue's (preferred) or band's default contact.
  const saveAsDefaultContact = async () => {
    if (!defaultContactCandidate) return;
    const target = defaultContactCandidate.venue || defaultContactCandidate.band;
    if (!target) return;
    const isVenue = !!defaultContactCandidate.venue;
    setSavingDefaultContact(true);
    try {
      const payload = {
        contact_name: formData.recipient_name || "",
        contact_email: formData.recipient_email || "",
      };
      if (isVenue) {
        await apiClient.put(`/venues/${target.id}`, payload);
      } else {
        await apiClient.put(`/bands/${target.id}`, payload);
      }
      toast.success(`Default contact saved for ${target.name}`);
      setDefaultContactCandidate(null);
    } catch (e) {
      toast.error("Failed to save default contact");
    } finally {
      setSavingDefaultContact(false);
    }
  };

  // --- Multi-gig bundle helpers --------------------------------------------

  // Resolved-recipient cache: {gigId -> {name, email, source}} populated lazily
  // by addGigToBundle so we don't re-fetch for gigs already in the bundle.
  const [resolvedRecipients, setResolvedRecipients] = useState({});

  // Resolve the invoice recipient for a gig, using the cache where possible.
  const resolveRecipient = async (gig) => {
    if (resolvedRecipients[gig.gig_id]) return resolvedRecipients[gig.gig_id];
    try {
      const r = (await apiClient.get(`/gigs/${gig.gig_id}/invoice-recipient`)).data;
      setResolvedRecipients(prev => ({ ...prev, [gig.gig_id]: r }));
      return r;
    } catch {
      return null;
    }
  };

  // Key used to decide if two gigs can share one invoice: the normalised resolved
  // recipient name (lowercased). Two gigs with the same resolved billing name
  // can be bundled regardless of how the resolver arrived at that name.
  const gigRecipientKey = (resolved) => {
    if (!resolved) return null;
    const name = (resolved.name || "").trim().toLowerCase();
    return name || null;
  };

  // Build the full line_items array from the current gig bundle. One "Performance
  // fee" line per gig, plus a per-gig expenses line if expenses > 0.
  const buildLineItemsFromGigs = (gigList) => {
    const items = [];
    for (const gig of gigList) {
      let gigDateStr = "";
      try { gigDateStr = format(parseISO(gig.date), "dd MMM yyyy"); } catch { gigDateStr = gig.date || ""; }
      if (gig.fee) {
        items.push({
          description: `Performance fee - ${gig.artist_name || "Performance"} at ${gig.venue_name || "Venue"}, ${gigDateStr}`,
          quantity: 1,
          unit_price: gig.fee,
          amount: gig.fee,
        });
      }
      if (gig.expenses && gig.expenses > 0) {
        items.push({
          description: `Travel & expenses — ${gig.venue_name || "gig"} ${gigDateStr}`.trim(),
          quantity: 1,
          unit_price: gig.expenses,
          amount: gig.expenses,
        });
      }
    }
    return items;
  };

  const addGigToBundle = async (gigId) => {
    if (!gigId || formData.gig_ids.includes(gigId)) return;
    const newGig = gigs.find(g => g.gig_id === gigId);
    if (!newGig) {
      toast.error("Gig not found");
      return;
    }
    // Compatibility check: resolve recipients for bundle + new gig, then compare.
    if (formData.gig_ids.length > 0) {
      const [newResolved, ...existingResolved] = await Promise.all([
        resolveRecipient(newGig),
        ...formData.gig_ids
          .map(id => gigs.find(g => g.gig_id === id))
          .filter(Boolean)
          .map(resolveRecipient),
      ]);
      const newKey = gigRecipientKey(newResolved);
      if (!newKey) {
        toast.error("This gig has no identifiable recipient — can't bundle it");
        return;
      }
      const existingKeys = new Set(existingResolved.map(gigRecipientKey));
      if (!existingKeys.has(newKey)) {
        toast.error("Can't bundle: that gig bills to a different recipient");
        return;
      }
    } else {
      // Prime the cache for the first gig so subsequent adds are fast.
      await resolveRecipient(newGig);
    }
    const newIds = [...formData.gig_ids, gigId];
    const bundle = newIds.map(id => gigs.find(g => g.gig_id === id)).filter(Boolean);
    setFormData(prev => ({
      ...prev,
      gig_ids: newIds,
      gig_id: newIds[0],
      line_items: buildLineItemsFromGigs(bundle),
    }));
  };

  const removeGigFromBundle = (gigId) => {
    const newIds = formData.gig_ids.filter(id => id !== gigId);
    const bundle = newIds.map(id => gigs.find(g => g.gig_id === id)).filter(Boolean);
    setFormData(prev => ({
      ...prev,
      gig_ids: newIds,
      gig_id: newIds[0] || "",
      // Rebuild line items unless the user has heavily customised them — for now
      // we always rebuild, matching the "auto-populate from gig" expectation.
      line_items: buildLineItemsFromGigs(bundle),
    }));
  };

  // -------------------------------------------------------------------------

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: "", quantity: 1, unit_price: 0, amount: 0 }]
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      
      // Recalculate amount
      if (field === "quantity" || field === "unit_price") {
        items[index].amount = (items[index].quantity || 0) * (items[index].unit_price || 0);
      }
      
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const vatAmount = subtotal * (formData.vat_rate / 100);
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipient_name) {
      toast.error("Please enter a recipient name");
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }
    
    setSaving(true);
    
    try {
      if (mode === "edit") {
        await apiClient.put(`/invoices/${id}`, {
          recipient_type: formData.recipient_type,
          recipient_name: formData.recipient_name,
          recipient_email: formData.recipient_email || null,
          recipient_address: formData.recipient_address || null,
          line_items: formData.line_items,
          vat_rate: formData.vat_rate,
          notes: formData.notes || null
        });
        toast.success("Invoice updated");
      } else {
        await apiClient.post("/invoices", {
          gig_id: formData.gig_id || null,           // back-compat: first gig of bundle
          gig_ids: formData.gig_ids,                  // full bundle (1+); backend prefers this
          recipient_type: formData.recipient_type,
          recipient_name: formData.recipient_name,
          recipient_email: formData.recipient_email || null,
          recipient_address: formData.recipient_address || null,
          issue_date: formData.issue_date,
          payment_terms_days: formData.payment_terms_days,
          line_items: formData.line_items,
          vat_rate: formData.vat_rate,
          currency: formData.currency,
          notes: formData.notes || null,
          invoice_number: formData.invoice_number?.trim() || null  // null = let server auto-generate
        });
        toast.success("Invoice created");
      }
      
      navigate("/invoices");
    } catch (error) {
      console.error("Invoice error:", error.response?.data || error);
      const errorMessage = error.response?.data?.detail || (mode === "edit" ? "Failed to update invoice" : "Failed to create invoice");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const { subtotal, vatAmount, total } = calculateTotals();
  const paymentDays = formData.payment_terms_days || settings?.payment_terms_days || 14;
  const dueDate = addDays(parseISO(formData.issue_date), paymentDays);

  return (
    <Layout>
      <div data-testid={mode === "edit" ? "edit-invoice-page" : "create-invoice-page"}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/invoices")} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-1">
              {mode === "edit" ? "Edit Invoice" : "New Invoice"}
            </p>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              {mode === "edit" ? "Edit Invoice" : "Create Invoice"}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
            {/* Linked Gig(s) — supports bundling multiple gigs onto one invoice
                as long as they share a recipient (same band, or same venue with
                no bands). Picker excludes already-linked gigs. */}
            {mode === "create" && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">Linked Gig(s) (Optional)</CardTitle>
                  <CardDescription>
                    Add one or more gigs that go to the same recipient — line items auto-populate.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Chips for currently linked gigs */}
                  {formData.gig_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.gig_ids.map(gigId => {
                        const g = gigs.find(x => x.gig_id === gigId);
                        if (!g) return null;
                        const dateLabel = g.date ? format(parseISO(g.date), "dd MMM yyyy") : "No date";
                        return (
                          <span
                            key={gigId}
                            className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs"
                            data-testid={`linked-gig-${gigId}`}
                          >
                            <span>
                              <strong>{g.artist_name || "Untitled"}</strong> @ {g.venue_name || "—"}
                              <span className="text-muted-foreground"> · {dateLabel} · £{g.fee}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeGigFromBundle(gigId)}
                              className="text-muted-foreground hover:text-foreground"
                              aria-label="Remove gig"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Picker for adding gigs (excludes already-linked ones) */}
                  {(() => {
                    const today = new Date().toISOString().split("T")[0];
                    const billableGigs = gigs.filter(g => g.fee && !formData.gig_ids.includes(g.gig_id));
                    const past = billableGigs
                      .filter(g => g.date && g.date <= today)
                      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
                    const future = billableGigs
                      .filter(g => g.date && g.date > today)
                      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
                    const undated = billableGigs.filter(g => !g.date);
                    const ordered = [...past, ...future, ...undated];

                    const items = ordered.map(gig => {
                      const dateLabel = gig.date ? format(parseISO(gig.date), "dd MMM yyyy") : "No date";
                      const isPast = gig.date && gig.date <= today;
                      return {
                        value: gig.gig_id,
                        label: `${gig.artist_name || "Untitled"} @ ${gig.venue_name || "Unknown venue"}`,
                        sublabel: `${dateLabel} · £${gig.fee}`,
                        keywords: [gig.artist_name, gig.venue_name, gig.date, dateLabel].filter(Boolean),
                        group: !gig.date ? "Undated" : isPast ? "Past gigs" : "Upcoming gigs",
                      };
                    });
                    return (
                      <SearchableSelect
                        items={items}
                        groupOrder={["Past gigs", "Upcoming gigs", "Undated"]}
                        value=""
                        onChange={(val) => addGigToBundle(val)}
                        placeholder={formData.gig_ids.length > 0 ? "Add another gig..." : "Add a gig..."}
                        searchPlaceholder="Search by artist, venue or date..."
                        emptyMessage="No more matching gigs"
                        testId="select-gig"
                        disabled={items.length === 0}
                      />
                    );
                  })()}
                  {formData.gig_ids.length > 1 && (
                    <p className="text-xs text-muted-foreground">
                      <strong>{formData.gig_ids.length} gigs</strong> bundled — all line items auto-populated. Remove or add more above.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Recipient */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Bill To</CardTitle>
                <CardDescription>
                  Select a recipient type and choose from your saved contacts or enter details manually
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Recipient picker: searchable, grouped by source.
                    Type selector hidden — backend `type=all` merges everything.
                    Group order per user preference: FA → Xero → Fretless. */}
                <div className="space-y-2">
                  <Label>Quick Fill from saved contacts</Label>
                  {(() => {
                    const groupLabel = (source) =>
                      source === "freeagent" ? "FreeAgent"
                      : source === "xero" ? "Xero"
                      : "Fretless (your gigs & collaborators)";
                    const badgeFor = (source) =>
                      source === "freeagent"
                        ? { label: "FA", color: "bg-emerald-500/10 text-emerald-400" }
                        : source === "xero"
                        ? { label: "XERO", color: "bg-sky-500/10 text-sky-400" }
                        : null;

                    const items = recipientSuggestions.map((s, idx) => ({
                      value: `${s.name}__${idx}`,  // unique key (names can duplicate across types)
                      label: s.name,
                      sublabel: s.email || s.address || undefined,
                      keywords: [s.email, s.address].filter(Boolean),
                      badge: badgeFor(s.source),
                      group: groupLabel(s.source),
                    }));

                    return (
                      <SearchableSelect
                        items={items}
                        groupOrder={["FreeAgent", "Xero", "Fretless (your gigs & collaborators)"]}
                        value=""  // ephemeral picker — selection just fills the fields below
                        onChange={(val) => {
                          const idx = parseInt(val.split("__").pop(), 10);
                          const suggestion = recipientSuggestions[idx];
                          if (suggestion) handleSelectSuggestion(suggestion);
                        }}
                        placeholder={
                          loadingSuggestions
                            ? "Loading..."
                            : recipientSuggestions.length
                            ? "Search saved contacts..."
                            : "No saved recipients yet"
                        }
                        searchPlaceholder="Type to search by name, email or address..."
                        emptyMessage="No matching contacts"
                        disabled={loadingSuggestions || recipientSuggestions.length === 0}
                        testId="recipient-suggestion"
                      />
                    );
                  })()}
                  <p className="text-xs text-muted-foreground">
                    Or fill in the details below manually.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.recipient_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, recipient_name: e.target.value }))}
                      required
                      data-testid="recipient-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.recipient_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, recipient_email: e.target.value }))}
                      data-testid="recipient-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.recipient_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipient_address: e.target.value }))}
                    data-testid="recipient-address"
                  />
                </div>

                {/* "Save as default" prompt — appears only when the linked gig's
                    venue/band has no saved contact AND the user has typed a name
                    AND they haven't dismissed the prompt for this gig before. */}
                {(() => {
                  if (!defaultContactCandidate) return null;
                  if (!formData.recipient_name?.trim()) return null;
                  const dismissed = (() => {
                    try {
                      return !!localStorage.getItem(`fretless:dismiss-default-${defaultContactCandidate.gigId}`);
                    } catch { return false; }
                  })();
                  if (dismissed) return null;
                  const target = defaultContactCandidate.venue || defaultContactCandidate.band;
                  if (!target?.name) return null;
                  return (
                    <div className="rounded-md border border-border bg-muted/30 p-3 text-sm flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">
                        Save <strong className="text-foreground">{formData.recipient_name}</strong> as the default contact for{" "}
                        <strong className="text-foreground">{target.name}</strong>?
                      </span>
                      <div className="flex gap-2 shrink-0">
                        <Button type="button" size="sm" variant="ghost" onClick={dismissDefaultContactPrompt}>
                          No
                        </Button>
                        <Button type="button" size="sm" onClick={saveAsDefaultContact} disabled={savingDefaultContact} data-testid="save-default-contact">
                          {savingDefaultContact ? "Saving..." : "Yes, save"}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Line Items</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.line_items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-2">
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, "description", e.target.value)}
                        data-testid={`line-item-desc-${index}`}
                      />
                    </div>
                    <div className="w-20 space-y-2">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, "quantity", parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.5"
                        data-testid={`line-item-qty-${index}`}
                      />
                    </div>
                    <div className="w-28 space-y-2">
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        data-testid={`line-item-price-${index}`}
                      />
                    </div>
                    <div className="w-28 text-right pt-2 font-mono">
                      £{item.amount?.toFixed(2) || "0.00"}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLineItem(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={addLineItem}
                  className="w-full"
                  data-testid="add-line-item"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Line Item
                </Button>

                {/* VAT */}
                <div className="flex items-center gap-4 pt-4 border-t border-border">
                  <Label>VAT Rate (%)</Label>
                  <Input
                    type="number"
                    value={formData.vat_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                    min="0"
                    max="100"
                    step="0.5"
                    data-testid="vat-rate"
                  />
                </div>

                {/* Totals */}
                <div className="pt-4 border-t border-border space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">£{subtotal.toFixed(2)}</span>
                  </div>
                  {formData.vat_rate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">VAT ({formData.vat_rate}%)</span>
                      <span className="font-mono">£{vatAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                    <span>Total</span>
                    <span className="font-mono text-primary">£{total.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment & Notes */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Payment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {mode === "create" && (
                  <div className="space-y-2">
                    <Label>Invoice Number</Label>
                    <Input
                      value={formData.invoice_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                      placeholder={suggestedInvoiceNumber || "Auto-generated"}
                      data-testid="invoice-number"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to use <code>{suggestedInvoiceNumber || "auto-generated"}</code>.
                      Change it here for a one-off; set a permanent reset in Settings → Invoices.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Issue Date</Label>
                    <Input
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, issue_date: e.target.value }))}
                      data-testid="issue-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={format(dueDate, 'yyyy-MM-dd')}
                      disabled
                      className="bg-secondary/50"
                    />
                    <p className="text-xs text-muted-foreground">
                      Based on {paymentDays} day payment terms
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    data-testid="invoice-notes"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate("/invoices")}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-invoice"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {mode === "edit" ? "Update Invoice" : "Create Invoice"}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default InvoiceForm;
;
